import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import debounce from 'lodash/debounce';
import abTestManager from '@zillow/home-details-abtest-manager';
import { useState, useEffect } from 'react';

var DATA_VIEW = 'ds-data-view';
var GDP_OVERRIDE_KEY = 'gdpDataOverride';
var ORIGINAL_GDP_CACHE = 'originalGdpCache'; // At 767px, we switch from side-by-side MediaStream and DataView mode
// to the stacked mode.

var MOBILE_VIEW_BREAKPOINT = 767;
var getIsMobileMode = function getIsMobileMode() {
  return typeof window !== 'undefined' && window.matchMedia("(max-width: " + MOBILE_VIEW_BREAKPOINT + "px)").matches;
};
/**
 * React hook returning whether the page is in mobile mode.
 * Responsive to window resizes (debounced to 100ms).
 * @returns {boolean}
 */

function useIsMobileMode() {
  var _useState = useState(getIsMobileMode),
      _useState2 = _slicedToArray(_useState, 2),
      isMobileMode = _useState2[0],
      setIsMobileMode = _useState2[1];

  useEffect(function () {
    var handleResize = debounce(function () {
      setIsMobileMode(getIsMobileMode());
    }, 100);

    window.addEventListener('resize', handleResize);
    return function () {
      return window.removeEventListener('resize', handleResize);
    };
  }, []);
  return isMobileMode;
} // Determine the total offsetTop of an element:
// https://stackoverflow.com/questions/1044988/getting-offsettop-of-element-in-a-table

function getOffsetTop(elem, parent) {
  var totalOffsetTop = 0;

  while (elem && elem !== parent) {
    totalOffsetTop += elem.offsetTop;
    elem = elem.offsetParent;
  }

  return totalOffsetTop;
}
/**
 * The function accepts a DOM node. The second arg
 * is the offset needed for the offsetTop, typically the top of element that is being scrolled.
 * The third arg is the option to scroll to the bottom of the node being requested
 * The fourth one is an optional elementID for the element to be scrolled
 * containerNode: This refers to the component where the scroll is happening. This is used in case
 *                of apps so that on clicking 'see less', the page scrolls to the top of the
 *                corresponding module.
 * */

function scrollTo(node, clientYOffset, toBottom, elementIdToScroll, containerNode) {
  if (window.ZMOB_nativeAPI && typeof window.ZMOB_nativeAPI.scrollTo === 'function' && containerNode) {
    window.ZMOB_nativeAPI.scrollTo(getOffsetTop(containerNode));
  } else {
    // The elementToScroll the element that is scroll function is applied to.
    // The default is the 'ds-data-view' if one is not passed as an argument.
    var id = elementIdToScroll || DATA_VIEW;
    var elementToScroll = document.getElementById(id);

    if (!elementToScroll || !node) {
      return;
    } // Grab the y-coordinate of the element (top or bottom) to bring into focus


    var nodeYLoc = toBottom ? getOffsetTop(node) + node.offsetHeight : getOffsetTop(node);
    var totalOffsetDistance = typeof clientYOffset === 'number' ? elementToScroll.getBoundingClientRect().top - clientYOffset : 0;
    elementToScroll.scrollTop = nodeYLoc - getOffsetTop(elementToScroll) + totalOffsetDistance;
  }
}
var HTML_CLASS_TO_REMOVE = 'zsg-lightbox_scroll';
var LightboxCloseScrollManager = {
  scrollX: 0,
  scrollY: 0,
  setScrollX: function setScrollX(scrollLoc) {
    if (typeof scrollLoc === 'number') {
      this.scrollX = scrollLoc;
    }
  },
  setScrollY: function setScrollY(scrollLoc) {
    if (typeof scrollLoc === 'number') {
      this.scrollY = scrollLoc;
    }
  },
  setScrollLocation: function setScrollLocation(scrollX, scrollY) {
    this.setScrollY(scrollY);
    this.setScrollX(scrollX);
  },
  scrollBody: function scrollBody(isLightboxHdp) {
    var _this = this;

    if (!isLightboxHdp && typeof window !== 'undefined') {
      // when .zsg-lightbox_scroll is added to the body, it causes the body
      // to scroll to the top. Removing the class allows us to replace the scroll
      // where we want
      document.documentElement.classList.remove(HTML_CLASS_TO_REMOVE); // This prevents the scroll taking place before the class is fully removed.
      // This was only a problem when this was called on the map lightbox on mobile devices.
      // When the scroll is called before it should, it locks up the scroll of the
      // media stream when closed. Ths problem can be seen in the video provided in HDP-15482.

      window.requestAnimationFrame(function () {
        window.scrollTo(_this.scrollX, _this.scrollY);
      });
    }
  }
};
var isEscKeyPress = function isEscKeyPress(e) {
  if (!e || e.defaultPrevented) {
    return false;
  }

  var key = e.key || e.keyCode;
  return key === 'Escape' || key === 'Esc' || key === 27;
}; // When closing a nested lightbox (a lightbox within the HDP lightbox),
// stopPropagation to prevent an ESC keypress from bubbling up and
// closing the main lightbox.
// Also blur the activeElement to reset the activeElement, so
// any following ESC keypresses target the correct element.

var onCloseNestedLightbox = function onCloseNestedLightbox(e) {
  if (e) {
    e.stopPropagation();
  }

  document && document.activeElement && document.activeElement.blur();
};
function showEarlyTriage(_temp) {
  var _ref = _temp === void 0 ? {} : _temp,
      isMobileApp = _ref.isMobileApp,
      variant = _ref.variant;

  var trialName = 'HDP_EARLY_TRIAGE_REORDER';

  if (isMobileApp) {
    trialName = trialName + "_APP";
  }

  if (variant) {
    return abTestManager.isTreatment(trialName, "VARIANT_" + variant);
  }

  return !abTestManager.isTreatment(trialName, ['CONTROL', null]);
}
var isSafariBrowserOrIOSWebview = function isSafariBrowserOrIOSWebview() {
  var _window$navigator, _window$navigator$use;

  var userAgent = typeof window !== 'undefined' && ((_window$navigator = window.navigator) === null || _window$navigator === void 0 ? void 0 : (_window$navigator$use = _window$navigator.userAgent) === null || _window$navigator$use === void 0 ? void 0 : _window$navigator$use.toLowerCase()) || '';
  return userAgent.indexOf('safari') >= 0 && userAgent.indexOf('chrome') < 0 && userAgent.indexOf('crios') < 0 || /(iPhone|iPod|iPad).*AppleWebKit(?!.*Safari)/i.test(userAgent);
};

export { DATA_VIEW, GDP_OVERRIDE_KEY, HTML_CLASS_TO_REMOVE, LightboxCloseScrollManager, ORIGINAL_GDP_CACHE, scrollTo as default, getIsMobileMode, getOffsetTop, isEscKeyPress, isSafariBrowserOrIOSWebview, onCloseNestedLightbox, showEarlyTriage, useIsMobileMode };
